import BaseView from '../views/BaseView';
import VideoPlayer from '../components/VideoPlayer';
import { getViewPercentage } from '../utils';
import throttle from 'lodash.throttle';
import gsap from 'gsap';

class Guide {
	constructor(element, button, background, parent) {
		// Elements
		this.parent = parent;
		this.element = element;
		this.button = button;
		this.background = background;
		this.content = this.element.querySelector('[data-guide-content]');
		this.sections = this.element.querySelectorAll('[data-section]');
		this.sectionNav = this.element.querySelector('[data-section-nav]');
		this.sectionMenuItems = this.element.querySelectorAll('[data-go-to-section]');
		this.backToTopButton = this.element.querySelector('[data-back-to-guide-top]');

		// State
		this.activeSection = null;
		this.active = false;
		this.breakpoint = window.matchMedia('(max-width:991px)');

		// Scroll progress
		this.boundUpdateProgress = this.updateProgress.bind(this);
		window.addEventListener('scroll', this.boundUpdateProgress);
		window.addEventListener('resize', this.boundUpdateProgress);
		this.updateProgress();

		this.boundDetermineActiveSection = throttle(this.determineActiveSection.bind(this), 300);
		window.addEventListener('scroll', this.boundDetermineActiveSection);
		window.addEventListener('resize', this.boundDetermineActiveSection);
		this.determineActiveSection();

		// Section navigation
		this.sectionNav?.addEventListener('click', (event) => {
			const target = event.target.closest('[data-go-to-section]');

			if (!target) {
				return;
			}

			this.goToSection(target.dataset.goToSection);
		});

		// Scroll to top
		this.backToTopButton?.addEventListener('click', () => {
			const top = this.content.getBoundingClientRect().top + window.scrollY;

			window.scrollTo({
				top: top - 180,
				behavior: 'smooth',
			});
		});

		this.element.addEventListener('click', (event) => {
			const target = event.target.closest('[data-section-toggle]');
			target && this.toggleSection(target.dataset.sectionToggle);
		});

		// Check if guide is already done
		if (localStorage.getItem(`guide-${this.element.dataset.guide}`) === 'done') {
			this.setDone();
		}
	}

	show() {
		if (this.active) {
			return;
		}

		this.element.classList.add('is-active');
		this.button.classList.add('is-active');
		this.background.classList.add('is-active');
		this.active = true;
		const duration = this.breakpoint.matches ? 1.5 : 1;

		return gsap.fromTo(this.content, { autoAlpha: 0 }, { autoAlpha: 1, duration: duration, ease: 'none' });
	}

	hide() {
		if (!this.active) {
			// In the case the background had a is-active class on page load
			if (this.background.classList.contains('is-active')) {
				this.background.classList.remove('is-active');
			}

			return;
		}

		// gsap.set(this.content, { autoAlpha: 0 });

		// this.element.classList.remove('is-active');
		this.button.classList.remove('is-active');
		this.background.classList.remove('is-active');
		this.sections.forEach((section) => section.classList.remove('is-active'));
		this.active = false;

		if (this.breakpoint.matches) {
			// Use a timeout because first all guides are set to inactive when activating a guide. Otherwise the active guides would always have a length of 0. After the timeout a guide could be active again.
			setTimeout(() => {
				const activeGuides = this.parent.guides.filter((guide) => guide.active);
				const firstGuide = this.parent.guides.find(Boolean);

				if (activeGuides.length == 0) {
					if (this !== firstGuide) {
						this.background.classList.remove('is-active');
					}

					firstGuide.background.classList.add('is-active');
				} else {
					this.background.classList.remove('is-active');
				}

				this.element.classList.remove('is-active');

				return;
			});
		} else {
			this.background.classList.remove('is-active');

			return gsap.fromTo(
				this.content,
				{ autoAlpha: 1 },
				{
					autoAlpha: 0,
					duration: 0.5,
					ease: 'none',
					onComplete: () => {
						this.element.classList.remove('is-active');
					},
				},
			);
		}
	}

	updateProgress() {
		if (!this.active || this.breakpoint.matches) {
			return;
		}

		const rect = this.content.getBoundingClientRect();
		const bottom = this.content.querySelector('.fseg-guide__bottom');
		const windowHeight = window.innerHeight;
		const articleHeight = this.content.offsetHeight - bottom.offsetHeight;
		const scrolled = Math.max(0, windowHeight - rect.top);
		const progress = Math.min(1, scrolled / articleHeight) * 100;

		this.button.style.setProperty('--scroll-progress', `${progress}%`);

		if (progress === 100) {
			this.setDone();
		}
	}

	determineActiveSection() {
		if (!this.active || this.breakpoint.matches) {
			return;
		}

		let hasMatch = false;
		let index = 0;

		while (!hasMatch && index < this.sections.length) {
			const section = this.sections[index];
			const percentage = getViewPercentage(section);

			if (percentage > 0) {
				this.activeSection = section;
				hasMatch = true;
				this.setActiveSection();
				break;
			}

			index++;
		}
	}

	setActiveSection() {
		this.sectionMenuItems.forEach((item) => {
			if (item.dataset.goToSection === this.activeSection.dataset.section) {
				item.classList.add('is-active');
			} else {
				item.classList.remove('is-active');
			}
		});
	}

	toggleSection(section) {
		this.sections.forEach((_section) => {
			if (_section.dataset.section == section) {
				_section.classList.toggle('is-active');
			}
		});
	}

	goToSection(sectionName) {
		const section = [...this.sections].find((section) => section.dataset.section === sectionName);

		if (!section) {
			return;
		}

		const top = section.getBoundingClientRect().top + window.scrollY;
		const navItem = [...this.sectionMenuItems].find((item) => item.dataset.goToSection === sectionName);

		window.scrollTo({
			top: top - 200 - navItem.offsetTop + 4,
			behavior: 'smooth',
		});
	}

	setDone() {
		this.button.querySelector('[data-guide-done]').classList.add('is-active');
		localStorage.setItem(`guide-${this.element.dataset.guide}`, 'done');
	}

	destroy() {
		window.removeEventListener('scroll', this.boundUpdateProgress);
		window.removeEventListener('resize', this.boundUpdateProgress);
		window.removeEventListener('scroll', this.boundDetermineActiveSection);
		window.removeEventListener('resize', this.boundDetermineActiveSection);
	}
}

export default class FilmSeriesEngage extends BaseView {
	constructor(container) {
		super(container);
		this.breakpoint = window.matchMedia('(max-width:991px)');
	}

	init() {
		const videos = this.container.querySelectorAll('.c-video-player video');

		videos.forEach((video) => {
			const videoPlayer = new VideoPlayer(video);
			videoPlayer.init();
		});

		// Guides
		this.guideElement = this.container.querySelector('[data-fseg-guide]');
		this.guideNav = this.container.querySelector('[data-guide-nav]');
		this.guideNavButtons = this.container.querySelectorAll('[data-show-guide]');
		this.guideBackgrounds = this.container.querySelectorAll('[data-guide-bg]');
		this.guides = [...this.container.querySelectorAll('[data-guide]')].map((guide) => {
			const navButton = this.guideNav.querySelector(`[data-show-guide="${guide.dataset.guide}"]`);
			const background = [...this.guideBackgrounds].find((background) => background.dataset.guideBg === guide.dataset.guide);
			return new Guide(guide, navButton, background, this);
		});

		this.container.addEventListener('click', (event) => {
			const target = event.target.closest('[data-show-guide]');
			target && this.handleButtonClick(target.dataset.showGuide);
		});

		this.container.addEventListener('click', (event) => {
			const target = event.target.closest('[data-open-guide]');
			target && this.handleButtonClick(target.dataset.openGuide);
		});

		this.container.addEventListener('click', (event) => {
			const target = event.target.closest('[data-toggle-guide-button]');
			target && this.handleButtonClick(target.dataset.toggleGuideButton);
		});

		this.container.addEventListener('click', (event) => {
			const target = event.target.closest('[data-close-guide]');
			target && this.handleButtonClick(target.dataset.closeGuide);
		});

		this.checkHash();
	}

	handleButtonClick(guideName) {
		const guide = this.guides.find((guide) => guide.element.dataset.guide === guideName);

		if (!guide) {
			return;
		}

		if (guide.active && this.breakpoint.matches) {
			guide.hide();
			history.replaceState(null, '', window.location.pathname + window.location.search);
			return;
		}

		this.activateGuide(guide);
		history.replaceState(null, '', `#${guide.element.dataset.guide}`);
	}

	async activateGuide(guide) {
		const activeGuide = this.guides.find((guide) => guide.active);

		if (activeGuide == guide) {
			return;
		}

		// this.guides.forEach((guide) => guide.hide());
		const offset = this.breakpoint.matches ? 0 : 238;

		window.scrollTo({
			top: this.guideElement.offsetTop + offset,
			behavior: 'instant',
		});

		if (activeGuide) {
			if (!this.breakpoint.matches) {
				await activeGuide.hide();
			} else {
				activeGuide.hide();
			}
		}
		guide.show();
	}

	checkHash() {
		const hash = window.location.hash;

		if (hash) {
			const guide = this.guides.find((guide) => guide.element.dataset.guide === hash.replace('#', ''));

			if (guide) {
				this.activateGuide(guide);
			}
		} else {
			!this.breakpoint.matches && this.guides.find(Boolean)?.show();
		}
	}

	destroy() {
		super.destroy();
		this.guides.forEach((guide) => guide.destroy());
	}
}
